import { CommonModule } from '@angular/common'
import { Component, inject, OnDestroy, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { debounceTime, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { FigureService } from 'src/app/service/data/figure.service'
import { NoteService } from 'src/app/service/data/note.service'
import { PairService } from 'src/app/service/data/pair.service'
import { QuizService } from 'src/app/service/data/quiz.service'
import { WordService } from 'src/app/service/data/word.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { DaydicTypesIconNames, Themes } from 'src/app/shared/enums/enums'
import { CanvasNodeType, CreateSubType } from '../models/canvas.model'

export interface ContentTypeOption {
  type: CanvasNodeType
  label: string
  icon: string
}

export interface AddCanvasElementResult {
  type: CanvasNodeType
  data: Record<string, any>
}

const CONTENT_TYPES: ContentTypeOption[] = [
  { type: 'create', label: 'Lingo AI', icon: 'create' },
  { type: 'word', label: 'Word', icon: DaydicTypesIconNames.WORD },
  { type: 'pair', label: 'Pair', icon: DaydicTypesIconNames.PAIR },
  { type: 'dictionary', label: 'Dictionary', icon: DaydicTypesIconNames.DICTIONARY },
  { type: 'note', label: 'Note', icon: DaydicTypesIconNames.NOTE },
  { type: 'figure', label: 'Figure', icon: DaydicTypesIconNames.FIGURE },
  { type: 'quiz', label: 'Quiz', icon: DaydicTypesIconNames.QUIZ },
  { type: 'attachment', label: 'Attachment', icon: 'attachment' },
]

export interface CreateOption {
  subType: CreateSubType
  label: string
  icon: string
}

const CREATE_OPTIONS: CreateOption[] = [
  { subType: 'dictionary', label: 'Create Dictionary', icon: DaydicTypesIconNames.DICTIONARY },
  { subType: 'note', label: 'Create Note', icon: DaydicTypesIconNames.NOTE },
  { subType: 'figure', label: 'Create Figure', icon: DaydicTypesIconNames.FIGURE },
  { subType: 'quiz', label: 'Create Quiz', icon: DaydicTypesIconNames.QUIZ },
]

@Component({
  selector: 'add-canvas-elements-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularMaterialsModules],
  templateUrl: './add-canvas-elements-dialog.component.html',
  styleUrls: ['./add-canvas-elements-dialog.component.scss'],
})
export class AddCanvasElementsDialogComponent implements OnDestroy {
  private dialogRef = inject(MatDialogRef<AddCanvasElementsDialogComponent>)
  private wordService = inject(WordService)
  private pairService = inject(PairService)
  private dictionaryService = inject(DictionaryService)
  private noteService = inject(NoteService)
  private figureService = inject(FigureService)
  private quizService = inject(QuizService)
  private themeService = inject(ThemeService)

  private destroy$ = new Subject<void>()
  private searchSubject = new Subject<string>()
  private fetchSubject = new Subject<void>()
  private themeSubscription: Subscription

  _isLightTheme = true
  _isDarkTheme = false
  _isPaperTheme = false

  contentTypes = CONTENT_TYPES
  createOptions = CREATE_OPTIONS
  selectedType = signal<CanvasNodeType | null>(null)
  searchQuery = ''
  items = signal<any[]>([])
  isLoading = signal(false)
  currentPage = 1
  totalCount = 0
  readonly pageSize = 20

  get logoSrc(): string {
    if (this._isDarkTheme) {
      return 'assets/media/logos/lingo_logo_dark.png'
    }
    return 'assets/media/logos/lingo_logo_light.png'
  }

  constructor() {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })

    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.currentPage = 1
      this.fetchSubject.next()
    })

    this.fetchSubject
      .pipe(
        switchMap(() => {
          const type = this.selectedType()
          if (!type) return of(null)
          this.isLoading.set(true)
          return this.searchQuery.trim()
            ? this.getSearchObservable(type)
            : this.getPageObservable(type)
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res: any) => {
          if (res === null) return
          if (res?.results) {
            this.handlePageResponse(res)
          } else {
            this.handleSearchResponse(res)
          }
        },
        error: () => this.handleError(),
      })
  }

  selectType(type: CanvasNodeType): void {
    this.selectedType.set(type)
    this.searchQuery = ''
    this.currentPage = 1
    this.fetchSubject.next()
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchQuery)
  }

  fetchItems(): void {
    this.fetchSubject.next()
  }

  private getPageObservable(type: CanvasNodeType): Observable<any> {
    switch (type) {
      case 'word':
        return this.wordService.get_words_page(this.currentPage, this.pageSize)
      case 'pair':
        return this.pairService.get_pairs_page(this.currentPage, this.pageSize)
      case 'dictionary':
        return this.dictionaryService.get_dics_page(this.currentPage, this.pageSize)
      case 'note':
        return this.noteService.get_notes_page(this.currentPage, this.pageSize)
      case 'figure':
        return this.figureService.get_figures()
      case 'quiz':
        return this.quizService.get_quizzes()
      default:
        return of(null)
    }
  }

  private getSearchObservable(type: CanvasNodeType): Observable<any> {
    const target = this.searchQuery.trim()
    switch (type) {
      case 'word':
        return this.wordService.get_searched_words(target)
      case 'pair':
        return this.pairService.get_searched_pairs(target)
      case 'dictionary':
        return this.dictionaryService.get_searched_dics(target)
      case 'note':
        return this.noteService.get_searched_notes(target)
      case 'figure':
        return this.figureService.get_searched_figures(target)
      case 'quiz':
        return this.quizService.get_searched_quizzes(target)
      default:
        return of(null)
    }
  }

  private handlePageResponse(res: any): void {
    if (res?.results) {
      this.items.set(res.results)
      this.totalCount = res.count ?? 0
    } else {
      const arr = Array.isArray(res) ? res : []
      this.items.set(arr)
      this.totalCount = arr.length
    }
    this.isLoading.set(false)
  }

  private handleSearchResponse(res: any): void {
    const results = Array.isArray(res) ? res : (res?.results ?? [])
    this.items.set(results)
    this.totalCount = results.length
    this.isLoading.set(false)
  }

  private handleError(): void {
    this.items.set([])
    this.isLoading.set(false)
  }

  get hasNextPage(): boolean {
    return this.currentPage * this.pageSize < this.totalCount
  }

  get hasPrevPage(): boolean {
    return this.currentPage > 1
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.currentPage++
      this.fetchItems()
    }
  }

  prevPage(): void {
    if (this.hasPrevPage) {
      this.currentPage--
      this.fetchItems()
    }
  }

  getItemTitle(item: any): string {
    // Search endpoints return a unified `label` field; paginated endpoints use type-specific fields.
    if (item.label) return item.label

    const type = this.selectedType()
    switch (type) {
      case 'word':
        return item.word || 'Untitled'
      case 'pair':
        return `${item.word_1?.word ?? ''} — ${item.word_2?.word ?? ''}`
      case 'dictionary':
        return item.title || 'Untitled'
      case 'note':
        return item.title || 'Untitled'
      case 'figure':
        return item.title || 'Untitled'
      case 'quiz':
        return item.title || 'Untitled'
      default:
        return 'Untitled'
    }
  }

  getItemIcon(): string {
    const type = this.selectedType()
    return CONTENT_TYPES.find(ct => ct.type === type)?.icon ?? ''
  }

  selectItem(item: any): void {
    const type = this.selectedType()
    if (!type) return

    const keyMap: Record<CanvasNodeType, string> = {
      word: 'word',
      pair: 'pair',
      dictionary: 'dictionary',
      note: 'note',
      figure: 'figure',
      quiz: 'quiz',
      create: 'create',
      attachment: 'attachment',
    }
    this.dialogRef.close({ type, data: { [keyMap[type]]: item } } as AddCanvasElementResult)
  }

  private readonly defaultPrompts: Record<CreateSubType, string> = {
    dictionary: 'Create a dictionary.',
    note: 'Create a note.',
    figure: 'Create a figure.',
    quiz: 'Create a quiz.',
  }

  selectCreateOption(subType: CreateSubType): void {
    this.dialogRef.close({
      type: 'create',
      data: { create: { subType, prompt: this.defaultPrompts[subType] } },
    } as AddCanvasElementResult)
  }

  selectAttachment(): void {
    this.dialogRef.close({
      type: 'attachment',
      data: { images: [] },
    } as AddCanvasElementResult)
  }

  close(): void {
    this.dialogRef.close()
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.dialogRef.close()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.themeSubscription?.unsubscribe()
  }
}
